import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { ArrowLeft, Search, XCircle, Filter, Pencil, Ban, RotateCcw, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, ChevronsRight, Trash2, FileSpreadsheet, Printer, Columns, Info, Tag } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { fmtDate, formatIDR, WO_STAGES, deriveWOStage, deriveWORisk, deriveWOFinancialStatus, WO_FINANCIAL_META, getWOInvoicePolicy, PROJECT_STATUS, CANCEL_REASON_LABELS, computeWOCashDue, deriveSphSubStatus, SPH_SUB_STATUS } from "@/lib/constants";
import { STAGE_GROUPS, STAGE_FILTER_OPTIONS, ACTION_BUCKETS, ACTION_BUCKET_META, deriveActionBucket } from "@/lib/woStageGroup";
import { buildExportRows, buildExportRowsV2, exportWOsToExcel, printWOsToPDF, sortForExport, sphColumnCount, woWaitDays } from "@/lib/woExport";
import WOExportDialog from "./WOExportDialog";
// 2026-06-20 (DUAL WORKFLOW): engine chevron canonical (mirror backend).
import { computeWoState, summarizeWoStates } from "@/lib/woWorkflow";
import WOStageInfoDialog from "./WOStageInfoDialog";
import SearchableSelect from "@/components/SearchableSelect";
import { useAccess } from "@/lib/access";
import { useAuth } from "@/lib/auth";
import { computeEntryRetention, tokenize, matchTokens } from "./projectHelpers";
// 149-cw-r120 (2026-08-29): AccurateUpdateMenu tidak lagi diimport di WOMasterDetail.
import CancelByOwnerDialog from "./CancelByOwnerDialog";
import ReopenCancelledDialog from "./ReopenCancelledDialog";
import WoCategoryBulkDialog from "./WoCategoryBulkDialog";
// 149-cw-r162: Tombol Close Project langsung di WO Detail supaya user tidak
// perlu kembali ke Projects tab utama. Pakai dialog yang SAMA dgn ProjectsPage.
import CloseProjectDialog from "@/components/CloseProjectDialog";
import { computeRetentionHeld, computeRetentionClaimed } from "./projectHelpers";
import AuditLogButton from "@/components/AuditLogButton";

/**
 * WOMasterDetailView — 149-bp. Master-Detail layout untuk Monitoring Working Order.
 *   Kiri  (65%): List WO — hanya 5 kolom utama (WO+Judul, Outlet, Tahap, Target).
 *   Kanan (35%): Detail panel sticky — identitas, timeline, komersial, tanggal penting,
 *                keuangan, dan Next Action + tombol Cancel by Owner / Reopen.
 * Toggle BOARD (WOMonitoringView legacy) tetap tersedia di parent (ProjectsPage).
 */
function outletKey(p) {
  return (p.parent_project_name || p.project_name || "—").trim() || "—";
}

const PAGE_SIZE = 20;

// R3 (2026-08-27) — Column visibility toggle.
// Kolom "wo" wajib on (identitas utama); lainnya opsional.
// Persist ke localStorage supaya pilihan user sticky lintas session.
const WO_COLUMNS = [
  { key: "po",        label: "No. PO",                    widthPct: 12 },
  { key: "wo",        label: "WO / Judul",                widthPct: 16, alwaysOn: true },
  { key: "outlet",    label: "Outlet",                    widthPct: 12 },
  { key: "stage",     label: "Tahap Saat Ini",            widthPct: 13 },
  { key: "action",    label: "Kategori WO",               widthPct: 12 },
  { key: "activity",  label: "Aktivitas Terakhir",        widthPct: 17 },
  { key: "wait_days", label: "Hari Menunggu",             widthPct: 8 },
  { key: "target",    label: "Tanggal Selesai Pekerjaan", widthPct: 10 },
];
const WO_COLS_STORAGE_KEY = "wmd_visible_cols_v1";

function deriveLastActivity(p) {
  // 149-cw-r96 (2026-08-28) — Aktivitas Terakhir kini mengambil milestone
  // dengan TANGGAL PALING BARU dari SEMUA tahapan lifecycle, bukan hanya
  // milestone yang match dengan __stage. Ini memastikan setelah user update
  // tanggal kegiatan (mis. tambah SPH v3), kolom Aktivitas Terakhir langsung
  // menampilkan versi terbaru + tanggal-nya. Status terminal (closed /
  // cancelled_by_owner) TETAP prioritas paling atas.
  if (p.status === "cancelled_by_owner") return { label: "Dibatalkan Owner", date: p.cancelled_at };
  if (p.status === "completed") return { label: "WO Closed", date: p.completed_at };

  const subs = Array.isArray(p.sph_submissions) ? p.sph_submissions.filter((s) => s?.date) : [];
  const latestSph = subs.length > 0
    ? [...subs].sort((a, b) => (a.date || "").localeCompare(b.date || "")).slice(-1)[0]
    : null;
  const entries = Array.isArray(p.income_entries) ? p.income_entries : [];
  const mainInvoices = entries.filter((e) => e && e.invoice_date && !e.is_retention_claim);
  const retInvoices = entries.filter((e) => e && e.invoice_date && e.is_retention_claim);
  const latestMainInvoice = mainInvoices.length > 0
    ? [...mainInvoices].sort((a, b) => (a.invoice_date || "").localeCompare(b.invoice_date || "")).slice(-1)[0]
    : null;
  const latestRetInvoice = retInvoices.length > 0
    ? [...retInvoices].sort((a, b) => (a.invoice_date || "").localeCompare(b.invoice_date || "")).slice(-1)[0]
    : null;

  // Kumpulkan kandidat milestone (label + date). Yg tidak punya date di-skip.
  const candidates = [
    p.wo_date || p.created_at ? { label: "WO diterima", date: p.wo_date || p.created_at } : null,
    p.site_survey_date ? { label: "Site survey selesai", date: p.site_survey_date } : null,
    latestSph ? { label: `SPH v${subs.length} dikirim`, date: latestSph.date } : (p.sph_submit_date ? { label: "SPH dikirim", date: p.sph_submit_date } : null),
    p.po_date || p.po_number ? { label: "PO diterima", date: p.po_date || null } : null,
    p.project_done_date ? { label: "Pekerjaan selesai", date: p.project_done_date } : null,
    latestMainInvoice ? { label: "Invoice diterbitkan", date: latestMainInvoice.invoice_date } : (p.invoice_sent_date ? { label: "Invoice dikirim", date: p.invoice_sent_date } : null),
    latestRetInvoice ? { label: "Invoice retensi diterbitkan", date: latestRetInvoice.invoice_date } : null,
  ].filter((c) => c && c.date);

  if (candidates.length === 0) return { label: "—", date: null };

  // Ambil kandidat dgn tanggal terbesar (paling baru). Bila tie, kandidat
  // yang lebih akhir di array (lebih ke arah invoice) menang.
  candidates.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return candidates[candidates.length - 1];
}

// R1.1 helpers (STAGE_GROUPS, stageMatches, STAGE_FILTER_OPTIONS, ACTION_BUCKETS,
// deriveActionBucket) dipindah ke `@/lib/woStageGroup` supaya bisa di-unit-test
// tanpa mount komponen. Lihat file tsb untuk semantik lengkapnya.

// 2026-06-20 (user request DUAL WORKFLOW) — Rekapitulasi chevron Monitoring WO
// memakai SATU engine canonical `@/lib/woWorkflow` (mirror persis backend
// utils/wo_workflow.py). MUTUALLY EXCLUSIVE: setiap WO masuk TEPAT SATU
// chevron; Closed & Dibatalkan tidak masuk chevron aktif; kategori kosong →
// "kategori_belum_ditentukan". State engine ditempel sekali sebagai `__wf`.
function woIsOverdue(w) {
  if (!w) return false;
  const st = (w.status || "").toLowerCase();
  if (st === "completed" || st === "cancelled_by_owner" || st === "cancelled") return false;
  const t = w.project_done_date || w.end_date;
  if (!t) return false;
  const d = new Date(t);
  if (isNaN(d.getTime())) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

function chevronBucketOf(w) {
  return w?.__wf?.chevron || "wo_diterima";
}

function chevronMatches(p, filter) {
  // 149-cw-r112 — filter bisa string atau array. Empty/"all" = tampilkan semua.
  if (!filter) return true;
  if (Array.isArray(filter)) {
    if (filter.length === 0) return true;
    const bucket = chevronBucketOf(p);
    return filter.includes(bucket);
  }
  if (filter === "all") return true;
  return chevronBucketOf(p) === filter;
}

export default function WOMasterDetailView({ projects, onOpenEdit, onChanged, onDelete }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canCancelWo = canPerm(user, "monitoring_wo", "delete") || canPerm(user, "projects", "delete");
  const canLedger = canPerm(user, "projects", "update");
  // 149-cw-r170: Close/Reopen WO — perm granular TERPISAH `projects.close`
  // (bukan `projects.delete`). Backend: PATCH /active-projects/{pid}/close|reopen.
  const canClose = canPerm(user, "projects", "close");
  const navigate = useNavigate();
  const handleBack = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    navigate("/projects?tab=wo");
  };

  // 149-cw-r6 (PROMPT 16-PROJECT-SELECTION-R2 B): baca deep-link `?wo=` +
  // `?mnt_parent=` dari URL. `wo` HARUS menjadi sumber pilihan utama saat tersedia.
  // Jangan auto-select item pertama jika URL punya wo id yang valid.
  const [urlParams, setUrlParams] = useSearchParams();
  const initialWoParam = urlParams.get("wo") || "";
  const initialMntParent = urlParams.get("mnt_parent") || "";
  const deepLinkHydratedRef = useRef(false);   // sudah pernah proses deep-link?
  const notFoundRef = useRef(null);            // last WO id yang tidak ditemukan

  const [q, setQ] = useState("");
  const [outletFilter, setOutletFilter] = useState("all");
  const [picFilter, setPicFilter] = useState("all");
  // 149-cw-r112 (2026-08-29) — stageFilter: array of stage keys (multi-select).
  // Empty array = "Semua Tahap" (no filter). Klik chevron = toggle stage
  // di array; dropdown = checkbox multi-select.
  const [stageFilter, setStageFilter] = useState(() => []);
  const [actionFilter, setActionFilter] = useState("all"); // R32F: kept for backward-compat, unused since r86
  // 149-cw-r86 (2026-08-27) — Urgent Case filter (all | urgent | non_urgent).
  const [urgentFilter, setUrgentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | aktif | cancelled_owner | closed
  // 149-cw-r86 — Info dialog per chevron.
  const [infoStage, setInfoStage] = useState(null);
  // Default sort: nomor PO ascending (user request 2026-08-27) — WO tanpa PO
  // (po_number kosong) muncul di paling bawah karena string kosong akan diurutkan
  // via `pick()` di bawah (kosong dianggap "zzzz" agar tenggelam di ascending).
  const [sort, setSort] = useState({ key: "po", dir: "asc" });
  const [selectedId, setSelectedId] = useState(null);
  // 149-cw-r98 (2026-08-28) — Multi-select checkbox untuk export subset.
  // Ketika ada WO terpilih, klik Print PDF / Export Excel akan menampilkan
  // konfirmasi: hanya WO terpilih vs semua WO terfilter.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // 149-cw-r99 (2026-08-28) — Dialog konfirmasi export dengan 3 tombol
  // (Batal · Cetak Semua · Cetak Terpilih). Mode: 'pdf' | 'xlsx'.
  const [exportDlg, setExportDlg] = useState({ open: false, mode: null });
  // 2026-06-20 (user request): dialog bulk "Tentukan Kategori WO" (admin).
  const [catDlg, setCatDlg] = useState(false);
  const [page, setPage] = useState(1);
  const [cancelDlg, setCancelDlg] = useState({ open: false, project: null });
  const [reopenDlg, setReopenDlg] = useState({ open: false, project: null });
  // 149-cw-r162: Close Project dialog state (reuse dari ProjectsPage flow).
  const [closeDlg, setCloseDlg] = useState({ open: false, project: null });

  // R3 (2026-08-27): Kolom visibility (persist ke localStorage).
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem(WO_COLS_STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.length > 0) {
        // Selalu paksa kolom alwaysOn tampil.
        const set = new Set(arr);
        for (const c of WO_COLUMNS) if (c.alwaysOn) set.add(c.key);
        return set;
      }
    } catch { /* ignore */ }
    return new Set(WO_COLUMNS.map((c) => c.key));
  });
  useEffect(() => {
    try { localStorage.setItem(WO_COLS_STORAGE_KEY, JSON.stringify([...visibleCols])); } catch { /* ignore */ }
  }, [visibleCols]);
  const toggleCol = (k) => {
    const col = WO_COLUMNS.find((c) => c.key === k);
    if (col?.alwaysOn) return;
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      if (next.size === 0) return prev; // minimal 1 kolom
      return next;
    });
  };
  const visibleColKeys = useMemo(() => WO_COLUMNS.filter((c) => visibleCols.has(c.key)).map((c) => c.key), [visibleCols]);
  const showCol = (k) => visibleCols.has(k);

  const wos = useMemo(() => projects.filter((p) => p.project_type === "working_order"), [projects]);

  // 16-PROJECT-PARENT-CLEANUP-R1-R1: outlet dropdown SOURCE = master parent
  // maintenance (bukan distinct dari WO). Parent aktif tanpa WO harus tetap
  // muncul di pilihan; empty-state ditampilkan saat parent 0 WO dipilih.
  const maintenanceParents = useMemo(
    () => projects.filter((p) =>
      p.project_type === "new_project" && p.is_outlet === true &&
      !p.parent_project_id && !p.is_archived   // 149-cw-r2: exclude archived
    ),
    [projects]
  );

  // Derived enrichment: attach stage + risk once.
  // R228 (2026-10-20, user request): fitur PENILAIAN RISIKO dinonaktifkan dari
  // tampilan — badge/filter/kolom risiko dihapus. `__risk` TETAP dihitung
  // internal karena dipakai bucket operasional "overdue" di woStageGroup
  // (bukan tampilan risiko); tidak ada data yang dihapus.
  const enriched = useMemo(() => wos.map((p) => ({
    ...p,
    __stage: deriveWOStage(p),
    __risk: deriveWORisk(p),
    __wf: computeWoState(p, woIsOverdue(p)),
  })), [wos]);

  // Unique lookup for filter dropdowns. Union of (a) master parent maintenance
  // list — supaya parent 0 WO tetap muncul — dan (b) distinct outletKey dari WO
  // legacy yang parentnya mungkin belum di-flag is_outlet=true.
  const outletList = useMemo(() => {
    const s = new Set();
    for (const p of maintenanceParents) s.add((p.project_name || "—").trim() || "—");
    for (const p of enriched) s.add(outletKey(p));
    return Array.from(s).sort((a, b) => a.localeCompare(b, "id"));
  }, [maintenanceParents, enriched]);
  const picList = useMemo(() => Array.from(new Set(enriched.map((w) => w.created_by_name).filter(Boolean))).sort(), [enriched]);

  // ---- Filtered list (R1.1) ----
  // ROOT CAUSE bug funnel/count sebelumnya:
  //   1) Chevron Invoice men-set stageFilter="invoice_95" (exact) padahal
  //      count-nya adalah gabungan invoice_full+invoice_95+invoice_5_retention.
  //   2) `kpi` dihitung dari `filtered` (sudah kena stageFilter/actionFilter)
  //      → klik satu chevron runtuh count chevron lain menjadi 0.
  // FIX: pisah pipeline jadi 2 lapis.
  //   `independentFiltered` = search + outlet + pic + status + risk (BUKAN
  //     stage/action). Digunakan sebagai basis SEMUA count (funnel + bucket).
  //   `filtered` = independentFiltered + stage + action. Digunakan untuk
  //     daftar & pagination.
  const independentFiltered = useMemo(() => {
    const tokens = tokenize(q);
    return enriched.filter((p) => {
      if (outletFilter !== "all" && outletKey(p) !== outletFilter) return false;
      if (picFilter !== "all" && p.created_by_name !== picFilter) return false;
      if (statusFilter === "aktif" && (p.status === "completed" || p.status === "cancelled_by_owner")) return false;
      if (statusFilter === "cancelled_owner" && p.status !== "cancelled_by_owner") return false;
      if (statusFilter === "closed" && p.status !== "completed") return false;
      if (tokens.length) {
        const hay = [p.wo_number, p.wo_title, p.po_number, outletKey(p), p.client, p.city, p.created_by_name].filter(Boolean).join(" ");
        if (!matchTokens(hay, tokens)) return false;
      }
      return true;
    });
  }, [enriched, q, outletFilter, picFilter, statusFilter]);

  const filtered = useMemo(() => independentFiltered.filter((p) => {
    if (!chevronMatches(p, stageFilter)) return false;
    if (actionFilter !== "all" && deriveActionBucket(p) !== actionFilter) return false;
    // 149-cw-r86 / 2026-06-20 (DUAL WORKFLOW): filter Kategori WO EKSPLISIT —
    // WO tanpa kategori (is_urgent kosong) TIDAK dianggap non-urgent; ia hanya
    // tampil di "Semua" atau filter "Belum Ditentukan".
    if (urgentFilter === "urgent" && p.is_urgent !== true) return false;
    if (urgentFilter === "non_urgent" && p.is_urgent !== false) return false;
    if (urgentFilter === "undetermined" && p.is_urgent !== null && p.is_urgent !== undefined) return false;
    return true;
  }), [independentFiltered, stageFilter, actionFilter, urgentFilter]);

  // ---- KPI (R1.1) — Faceted counts dari `independentFiltered`.
  // Count MENGABAIKAN stageFilter/actionFilter sehingga klik satu chevron
  // tidak meruntuhkan count chevron lain.
  const kpi = useMemo(() => {
    const k = {
      total_aktif: 0,
      // 149-cw-r86 — Urgent Case counts (replace Perlu Tindakan buckets).
      // 2026-06-20 (DUAL WORKFLOW): kategori EKSPLISIT — is_urgent kosong
      // (null/undefined) dihitung terpisah sebagai "Belum Ditentukan".
      urgent_count: 0, non_urgent_count: 0, undetermined_count: 0,
      // Perlu Tindakan bucket counts (kept for backward compat/tests but not rendered)
      bkt_belum_survey: 0, bkt_belum_sph: 0, bkt_menunggu_po: 0, bkt_po_belum_mulai: 0,
      bkt_pengerjaan: 0, bkt_selesai_belum_invoice: 0,
      bkt_retensi_belum: 0, bkt_overdue: 0, bkt_cancelled_owner: 0,
    };
    for (const w of independentFiltered) {
      if (w.status !== "completed" && w.status !== "cancelled_by_owner") k.total_aktif += 1;
      // 2026-06-19 (user request): Kategori WO menghitung SEMUA WO dalam scope,
      // TERMASUK yang sudah closed dan cancelled_by_owner.
      if (w.is_urgent === true) k.urgent_count += 1;
      else if (w.is_urgent === false) k.non_urgent_count += 1;
      else k.undetermined_count += 1;
      // Bucket count — pakai deriveActionBucket (predicate identik dgn filter).
      const bkt = deriveActionBucket(w);
      switch (bkt) {
        case "belum_survey":          k.bkt_belum_survey += 1; break;
        case "belum_sph":             k.bkt_belum_sph += 1; break;
        case "menunggu_po":           k.bkt_menunggu_po += 1; break;
        case "po_belum_mulai":        k.bkt_po_belum_mulai += 1; break;
        case "pengerjaan":            k.bkt_pengerjaan += 1; break;
        case "selesai_belum_invoice": k.bkt_selesai_belum_invoice += 1; break;
        case "retensi_belum":         k.bkt_retensi_belum += 1; break;
        case "overdue":               k.bkt_overdue += 1; break;
        case "cancelled_owner":       k.bkt_cancelled_owner += 1; break;
        default: break;
      }
    }
    return k;
  }, [independentFiltered]);

  // 2026-06-20 (DUAL WORKFLOW): ringkasan chevron EKSKLUSIF dari engine —
  // Σ chevron aktif + Closed + Dibatalkan = total WO dalam scope filter.
  const wfSummary = useMemo(
    () => summarizeWoStates(independentFiltered.map((w) => w.__wf)),
    [independentFiltered]
  );

  // ---- Sorting ----
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    const pick = (p) => {
      switch (sort.key) {
        // "po" sort: PO kosong → "zzzz" supaya di ascending muncul di paling bawah
        // (user ingin WO yang sudah punya PO tampil dulu, urut nomor PO).
        case "po": return (p.po_number || "").trim().toLowerCase() || "zzzz";
        case "wo": return (p.wo_number || "").toLowerCase();
        case "outlet": return outletKey(p).toLowerCase();
        case "stage": return WO_STAGES.findIndex((s) => s.key === p.__stage);
        case "target": return p.project_done_date || "";
        case "wo_date":
        default: return p.wo_date || "";
      }
    };
    arr.sort((a, b) => { const va = pick(a); const vb = pick(b); if (va < vb) return -1 * dir; if (va > vb) return 1 * dir; return 0; });
    return arr;
  }, [filtered, sort]);

  // ---- Pagination (client-side, hemat request) ----
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = useMemo(() => sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [sorted, page]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  // ============================================================================
  // 16-WO-SELECTION-LOOP-P0 — Selection state machine (single source of truth).
  //
  // AKAR BUG SEBELUMNYA (v6/v7): dua effect terpisah membaca `urlParams.get("wo")`
  // pada setiap render, sehingga saat user klik WO B → `setSelectedId(B)` → effect
  // re-run dgn urlParams masih `?wo=A` (belum flush) → `setSelectedId(A)` = reset.
  //
  // AKAR BUG SEKUNDER (v14 attempt 1): hydration + fallback effect terpisah,
  // keduanya jalan dalam batch yg sama saat `enriched` pertama kali terisi.
  // React batches `setSelectedId(target)` dari hydration DAN `setSelectedId(sorted[0])`
  // dari fallback → yang belakangan menang (sorted[0]), deep-link hilang.
  //
  // FIX: Satukan hydration + fallback ke SATU effect. Setiap render hanya boleh
  // memanggil setSelectedId SEKALI. URL parameter `?wo=` HANYA dibaca saat
  // hydration pertama. Setelah itu `selectedId` = otoritas absolut.
  // ============================================================================
  useEffect(() => {
    if (sorted.length === 0) {
      if (!deepLinkHydratedRef.current && initialWoParam && enriched.length > 0 && !enriched.some((w) => w.id === initialWoParam)) {
        notFoundRef.current = initialWoParam;
        deepLinkHydratedRef.current = true;
      }
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!deepLinkHydratedRef.current) {
      // Deep-link hydration (SEKALI). Tunggu enriched terisi supaya bisa cari target.
      if (enriched.length === 0) return;
      if (initialMntParent) {
        const parent = projects.find((p) => p.id === initialMntParent);
        if (parent && parent.project_name) setOutletFilter(parent.project_name.trim() || "all");
      }
      if (initialWoParam) {
        const target = enriched.find((w) => w.id === initialWoParam);
        if (target) {
          deepLinkHydratedRef.current = true;
          if (selectedId !== initialWoParam) setSelectedId(initialWoParam);
          return; // JANGAN lanjut ke fallback dalam batch yg sama
        }
        notFoundRef.current = initialWoParam;
      }
      deepLinkHydratedRef.current = true;
      // Fall-through ke fallback bila tidak ada deep-link atau target tidak ditemukan.
    }
    // Fallback: pilih item pertama HANYA bila `selectedId` sudah tidak valid di `sorted`.
    // Menggunakan `sorted` (bukan `paged`) supaya pindah halaman tidak menendang seleksi.
    if (selectedId && sorted.some((p) => p.id === selectedId)) return;
    if (notFoundRef.current === initialWoParam && initialWoParam) return; // wo-not-found panel
    // 16-PROJECT-WO-CLICK-R1: bila initialWoParam MASIH VALID di enriched, jangan
    // fallback — hydration seharusnya set selectedId ke sana. Ini mencegah race
    // saat React StrictMode double-invokes effect: run pertama set
    // deepLinkHydratedRef=true tapi setSelectedId belum commit, run kedua sees
    // ref=true + selectedId=null → fallback clobber hydration intent.
    if (initialWoParam && enriched.some((w) => w.id === initialWoParam)) return;
    setSelectedId(sorted[0].id);
  }, [enriched, sorted, selectedId, initialWoParam, initialMntParent, projects]);

  // Sync URL — `selectedId` sbg source of truth, URL follower.
  // JANGAN masukkan `urlParams` ke deps: URL berubah karena effect ini, sehingga
  // include-nya akan trigger re-run tanpa sebab & rentan race dgn effect fallback.
  // 16-PROJECT-WO-CLICK-R1: guard `sorted.some(...)` — hanya tulis URL bila
  // selectedId ada di sorted list saat ini. Ini mencegah race saat hydration
  // dari deep-link Summary Project (mount ganda / batching state) menuliskan
  // URL dengan selectedId yang stale (WO lain dari fallback sebelumnya).
  useEffect(() => {
    if (!deepLinkHydratedRef.current) return;
    if (!selectedId) return;
    const sel = enriched.find((w) => w.id === selectedId);
    if (!sel) return;
    if (!sorted.some((w) => w.id === selectedId)) return;
    const cur = urlParams.get("wo") || "";
    if (cur === selectedId) return;
    const next = new URLSearchParams(urlParams);
    next.set("wo", selectedId);
    if (sel.parent_project_id) next.set("mnt_parent", sel.parent_project_id);
    setUrlParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, enriched, sorted]);

  // Pagination auto-jump: bila selectedId berada di halaman lain (mis. dari
  // deep-link atau filter change yg mengubah sorted), geser page supaya baris
  // yang aktif tetap terlihat. Scroll ke baris juga dijadwalkan.
  const pageJumpDoneRef = useRef(false);
  useEffect(() => {
    if (!deepLinkHydratedRef.current) return;
    if (!selectedId) return;
    const idx = sorted.findIndex((w) => w.id === selectedId);
    if (idx < 0) return;
    const targetPage = Math.floor(idx / PAGE_SIZE) + 1;
    if (page !== targetPage) {
      setPage(targetPage);
      pageJumpDoneRef.current = false;
      return;
    }
    if (pageJumpDoneRef.current) return;
    const row = document.querySelector(`[data-testid="wmd-row-${selectedId}"]`);
    if (row) requestAnimationFrame(() => row.scrollIntoView({ block: "center", behavior: "smooth" }));
    pageJumpDoneRef.current = true;
  }, [selectedId, sorted, page]);

  useEffect(() => { pageJumpDoneRef.current = false; }, [selectedId]);

  const selected = useMemo(() => enriched.find((p) => p.id === selectedId) || null, [enriched, selectedId]);

  // Handler klik baris — synchronous update; URL akan follow via effect di atas.
  const handleSelectWo = (id) => {
    if (id === selectedId) return;
    setSelectedId(id);
  };

  // 16-WO-PAGINATION-RBAC-R1 — Page navigation handler.
  // ROOT CAUSE bug "Berikutnya" tidak berpindah: effect auto-jump (below) mem-
  // paksa `page` mengikuti page dari `selectedId`. Saat user klik Berikutnya
  // (setPage 1→2) tapi selectedId masih di page 1 → auto-jump `setPage(1)` →
  // snap-back. Fix: saat user pindah page manual, sinkron-kan selectedId ke
  // item pertama halaman baru (atau null bila kosong) supaya invariant
  // "selectedId ada di current page" tetap terjaga & auto-jump idempotent.
  const goToPage = (target) => {
    const clamped = Math.max(1, Math.min(totalPages, target));
    if (clamped === page) return;
    setPage(clamped);
    const start = (clamped - 1) * PAGE_SIZE;
    const pageItems = sorted.slice(start, start + PAGE_SIZE);
    if (pageItems.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    const stillVisible = selectedId && pageItems.some((p) => p.id === selectedId);
    if (!stillVisible) setSelectedId(pageItems[0].id);
  };

  const resetFilters = () => {
    setQ(""); setOutletFilter("all"); setPicFilter("all"); setStageFilter([]);
    setActionFilter("all"); setStatusFilter("all"); setUrgentFilter("all");
  };

  const SortBtn = ({ k, label }) => (
    <button type="button" onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))}
      data-testid={`wmd-sort-${k}`}
      className="inline-flex items-center gap-1 text-left w-full uppercase text-[10px] tracking-wider font-semibold text-slate-600 hover:text-[#FF5A1F]">
      {label} {sort.key === k ? (sort.dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-30" />}
    </button>
  );

  // 2026-06-20 (user request DUAL WORKFLOW): daftar chevron = engine canonical
  // (9 chevron aktif EKSKLUSIF + Closed + Dibatalkan Owner). Tidak ada lagi
  // chevron kumulatif — satu WO hanya dihitung di SATU chevron.
  // URGENT CASE: Pengerjaan & Selesai Fisik didahulukan sebelum SPH & Negosiasi.
  const wfCounts = wfSummary.chevronCounts || {};
  const CHEVRON_DEFS = {
    wo_diterima:       { key: "wo_diterima",       label: "WO Diterima",         count: wfCounts.wo_diterima || 0,        filter: "wo_diterima" },
    site_survey:       { key: "site_survey",       label: "Site Survey",         count: wfCounts.site_survey || 0,        filter: "site_survey" },
    sph_negosiasi:     { key: "sph_negosiasi",     label: "SPH & Negosiasi",     count: wfCounts.sph_negosiasi || 0,      filter: "sph_negosiasi" },
    pengerjaan:        { key: "pengerjaan",        label: "Pengerjaan",          count: wfCounts.pengerjaan || 0,         filter: "pengerjaan" },
    selesai_fisik:     { key: "selesai_fisik",     label: "Selesai Fisik",       count: wfCounts.selesai_fisik || 0,      filter: "selesai_fisik" },
    pembuatan_invoice: { key: "pembuatan_invoice", label: "Pembuatan Invoice",   count: wfCounts.pembuatan_invoice || 0,  filter: "pembuatan_invoice" },
    pengiriman_invoice:{ key: "pengiriman_invoice",label: "Pengiriman Invoice",  count: wfCounts.pengiriman_invoice || 0, filter: "pengiriman_invoice" },
    invoice_pending:   { key: "invoice_pending",   label: "Invoice / Menunggu Penyelesaian", count: wfCounts.invoice_pending || 0, filter: "invoice_pending" },
    kategori_belum_ditentukan: { key: "kategori_belum_ditentukan", label: "Kategori Belum Ditentukan", count: wfCounts.kategori_belum_ditentukan || 0, filter: "kategori_belum_ditentukan", tone: "cancelled" },
    closed:            { key: "closed",            label: "Closed",              count: wfSummary.closed,                 filter: "closed", tone: "closed" },
    cancelled_owner:   { key: "cancelled_owner",   label: "Dibatalkan Owner",    count: wfSummary.dibatalkan,             filter: "cancelled_owner", tone: "cancelled" },
  };
  const CHEVRON_ORDER_NORMAL = ["wo_diterima", "site_survey", "sph_negosiasi", "pengerjaan", "selesai_fisik", "pembuatan_invoice", "pengiriman_invoice", "invoice_pending", "kategori_belum_ditentukan", "closed", "cancelled_owner"];
  const CHEVRON_ORDER_URGENT = ["wo_diterima", "site_survey", "pengerjaan", "selesai_fisik", "sph_negosiasi", "pembuatan_invoice", "pengiriman_invoice", "invoice_pending", "kategori_belum_ditentukan", "closed", "cancelled_owner"];
  const chevronOrder = urgentFilter === "urgent" ? CHEVRON_ORDER_URGENT : CHEVRON_ORDER_NORMAL;

  return (
    <div className="space-y-4" data-testid="wo-master-detail">
      {/* 2026-06-20: dialog bulk Tentukan Kategori (admin, preview + audit log). */}
      <WoCategoryBulkDialog
        open={catDlg}
        onOpenChange={setCatDlg}
        wos={enriched.filter((p) => selectedIds.has(p.id))}
        onDone={() => {
          setCatDlg(false);
          setSelectedIds(new Set());
          if (typeof onChanged === "function") onChanged();
        }}
      />
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          data-testid="monitoring-wo-back-button"
          className="rounded-none border-slate-400 text-[#0B1D3A] hover:bg-slate-100 uppercase text-xs tracking-wider font-semibold"
        >
          <ArrowLeft size={14} /> Kembali
        </Button>
      </div>

      {/* 149-cw-r89: Page header (breadcrumb + judul + subtitle) sekarang
          di-render di tab bar row (ProjectsPage) untuk hemat ruang vertikal.
          Header block yang dulu di sini dihapus. */}

      {/* 2026-06-19 (user request): Kategori WO dipindah ke ATAS chevron &
          dijadikan 3 kotak (Urgent Case / Non Urgent Case / All WO). Total
          mencakup WO cancelled_by_owner & closed (lihat perhitungan kpi). */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">Kategori WO</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-w-3xl" data-testid="wmd-urgent-cards">
          <BucketCard
            label="Urgent Case"
            value={kpi.urgent_count}
            active={urgentFilter === "urgent"}
            onClick={() => setUrgentFilter(urgentFilter === "urgent" ? "all" : "urgent")}
            testid="bkt-urgent"
            tone="rose"
          />
          <BucketCard
            label="Non Urgent Case"
            value={kpi.non_urgent_count}
            active={urgentFilter === "non_urgent"}
            onClick={() => setUrgentFilter(urgentFilter === "non_urgent" ? "all" : "non_urgent")}
            testid="bkt-non-urgent"
          />
          <BucketCard
            label="Belum Diklasifikasikan"
            value={kpi.undetermined_count}
            active={urgentFilter === "undetermined"}
            onClick={() => setUrgentFilter(urgentFilter === "undetermined" ? "all" : "undetermined")}
            testid="bkt-undetermined"
          />
          <BucketCard
            label="All WO"
            value={independentFiltered.length}
            active={urgentFilter === "all"}
            onClick={() => setUrgentFilter("all")}
            testid="bkt-all-wo"
          />
        </div>
      </div>

      {/* 149-cw-r108 (2026-08-28): Chevron grid dijadikan 2 baris × 5 kolom
          supaya masing-masing chevron lebih lebar & mudah dibaca. */}
      <div
        className="grid grid-cols-2 md:grid-cols-5 gap-1"
        style={{ gridTemplateColumns: undefined }}
        data-testid="wmd-stage-chevrons"
      >
        <style>{`@media (min-width: 768px) { [data-testid='wmd-stage-chevrons'] { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; } }`}</style>
        {chevronOrder.map((ck) => CHEVRON_DEFS[ck]).map((s) => {
          const isActive = stageFilter.includes(s.filter);
          // 149-cw-r110 (2026-08-28) — Simplified palette:
          //   Default (idle) card: putih dgn border slate + text navy.
          //   Selected/active card: navy full.
          //   Closed chevron: hijau (semantic done).
          //   Dibatalkan Owner: abu-abu (semantic cancelled).
          const bg =
            isActive              ? "bg-[#0B1D3A] text-white border border-[#0B1D3A]" :
            s.tone === "closed"   ? "bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100" :
            s.tone === "cancelled"? "bg-slate-100 text-slate-500 border border-slate-300 hover:bg-slate-200" :
                                    "bg-white text-[#0B1D3A] border border-slate-300 hover:bg-slate-50";
          const onChevronClick = () => {
            setStageFilter((prev) =>
              prev.includes(s.filter) ? prev.filter((k) => k !== s.filter) : [...prev, s.filter]
            );
          };
          return (
            <div
              key={s.key}
              role="button"
              tabIndex={0}
              onClick={onChevronClick}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChevronClick(); } }}
              data-testid={`wmd-chevron-${s.key}`}
              className={`relative overflow-hidden text-left px-3 py-2 cursor-pointer transition-all hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B1D3A] ${bg}`}
              style={{ clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)" }}
              title={`Filter: ${s.label}${s.count ? ` (${s.count})` : ""}`}
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider leading-tight pl-2 truncate pr-8">{s.label}</div>
              <div className="text-lg font-bold tabular-nums pl-2 leading-tight">{s.count}</div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setInfoStage({ key: s.key, label: s.label }); }}
                data-testid={`wmd-chevron-${s.key}-info`}
                title={`Informasi: ${s.label}`}
                className={`absolute top-1 right-3 flex items-center justify-center w-5 h-5 border rounded-full text-[10px] font-bold font-serif transition-colors ${isActive ? "border-white/60 text-white/90 hover:bg-white/25" : "border-slate-400 text-slate-500 hover:bg-slate-100"}`}
              >
                i
              </button>
            </div>
          );
        })}
      </div>

      {/* Legacy KPI Row (kept small/hidden — buckets di atas menggantikan) */}
      <div className="hidden" data-testid="wmd-kpi-row">
        <KpiCard label="WO Aktif" value={kpi.total_aktif} tone="slate" testid="kpi-aktif" />
        <KpiCard label="Terlambat" value={kpi.overdue} tone="rose" testid="kpi-overdue" />
        <KpiCard label="SPH / Negosiasi / PO" value={kpi.sph_po} tone="amber" testid="kpi-wait-po" />
        <KpiCard label="Pengerjaan" value={kpi.pengerjaan} tone="orange" testid="kpi-wip" />
        <KpiCard label="Belum Invoice" value={kpi.belum_invoice} tone="sky" testid="kpi-no-invoice" />
        <KpiCard label="Dibatalkan Owner" value={kpi.cancelled} tone="slate" testid="kpi-cancelled" />
      </div>

      {/* Toolbar export + Sync Nama Parent (BOD Lv1) di kiri tombol Kolom. */}
      <div className="flex items-center gap-2 flex-wrap justify-between -mt-1" data-testid="wmd-global-export-toolbar">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Export Daftar Aktif</div>
        <div className="flex items-center gap-1.5 flex-wrap">
        {user?.division === "BOD" && Number(user?.level) === 1 && (
          <Button
            data-testid="btn-sync-child-names"
            variant="outline"
            className="rounded-none h-8 px-3 border-sky-500 text-sky-700 hover:bg-sky-50 uppercase text-[10px] tracking-wider font-semibold"
            onClick={async () => {
              if (!window.confirm(
                "Sync Child Project Names (Admin):\n\n" +
                "Aksi ini fix WO/VO yang project_name / parent_project_name / client / lokasi tidak match dgn parent-nya.\n\n" +
                "Root cause: session 149-aj & 149-ak dulu lupa sync `project_name` saat pindah parent — WO tampilkan nama outlet lama meski `parent_project_id` sudah pindah.\n\n" +
                "Idempotent — kalau sudah sinkron, tidak ada perubahan.\n\nLanjut?"
              )) return;
              try {
                const { data } = await api.post("/admin/sync-child-project-names");
                if (data.updated === 0) {
                  toast.success("✅ Sudah sinkron — tidak ada WO/VO yang perlu di-fix.");
                } else {
                  const fields = Object.entries(data.updates_by_field || {})
                    .map(([k, n]) => `${k}: ${n}`).join(" · ");
                  toast.success(`✅ ${data.updated} WO/VO di-fix dari ${data.scanned} di-scan (${fields})`);
                }
                if (typeof onChanged === "function") onChanged();
              } catch (e) {
                toast.error(formatDetail(e.response?.data?.detail) || "Sync gagal");
              }
            }}
          >
            Sync Nama Parent (BOD)
          </Button>
        )}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="wmd-column-picker-trigger"
                className="inline-flex items-center gap-1 h-8 px-3 text-[10px] uppercase tracking-wider font-semibold border border-slate-400 text-slate-700 hover:bg-slate-100 transition-colors"
                title="Pilih kolom yang ditampilkan di tabel & cetak"
              >
                <Columns size={12} /> Kolom ({visibleColKeys.length}/{WO_COLUMNS.length})
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 rounded-none p-2" data-testid="wmd-column-picker-panel">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2 px-1">Tampilkan Kolom</div>
              <div className="space-y-0.5 max-h-72 overflow-y-auto">
                {WO_COLUMNS.map((c) => {
                  const on = visibleCols.has(c.key);
                  return (
                    <label
                      key={c.key}
                      className={`flex items-center gap-2 px-2 py-1.5 text-[11px] cursor-pointer select-none ${c.alwaysOn ? "opacity-60 cursor-not-allowed" : "hover:bg-slate-50"}`}
                      data-testid={`wmd-col-toggle-${c.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={!!c.alwaysOn}
                        onChange={() => toggleCol(c.key)}
                        className="h-3.5 w-3.5 accent-[#FF5A1F]"
                      />
                      <span className="text-slate-800 flex-1">{c.label}</span>
                      {c.alwaysOn && <span className="text-[9px] uppercase tracking-wider text-slate-400">wajib</span>}
                    </label>
                  );
                })}
              </div>
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-200">
                <button
                  type="button"
                  data-testid="wmd-col-toggle-all"
                  onClick={() => setVisibleCols(new Set(WO_COLUMNS.map((c) => c.key)))}
                  className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#FF5A1F]"
                >
                  Semua
                </button>
                <button
                  type="button"
                  data-testid="wmd-col-toggle-min"
                  onClick={() => setVisibleCols(new Set(WO_COLUMNS.filter((c) => c.alwaysOn).map((c) => c.key)))}
                  className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#FF5A1F]"
                >
                  Minimal
                </button>
              </div>
            </PopoverContent>
          </Popover>
          <button
            type="button"
            onClick={() => {
              // 2026-06-21 (user request): SELALU tampilkan dialog pilihan kolom
              // (bukan hanya saat ada WO terpilih).
              setExportDlg({ open: true, mode: "xlsx" });
            }}
            data-testid="wmd-export-xlsx"
            className="inline-flex items-center gap-1.5 h-8 px-3 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-[#0B1D3A] hover:bg-slate-100 transition-colors"
            title={`Export ${sorted.length} WO ke Excel — pilih kolom dulu (mengikuti filter aktif)${selectedIds.size > 0 ? ` — ${selectedIds.size} WO ditandai` : ""}`}
          >
            <FileSpreadsheet size={13} className="text-emerald-600" /> Export Excel
          </button>
          <button
            type="button"
            onClick={() => {
              setExportDlg({ open: true, mode: "pdf" });
            }}
            data-testid="wmd-export-pdf"
            className="inline-flex items-center gap-1.5 h-8 px-3 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-[#0B1D3A] hover:bg-slate-100 transition-colors"
            title={`Print ${sorted.length} WO sebagai PDF — pilih kolom dulu (mengikuti filter aktif)${selectedIds.size > 0 ? ` — ${selectedIds.size} WO ditandai` : ""}`}
          >
            <Printer size={13} className="text-rose-600" /> Print PDF
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="wmd-filter-bar">
        <div className="relative flex items-center">
          <Search size={13} className="absolute left-2.5 text-slate-400 pointer-events-none" />
          <input
            data-testid="wmd-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari WO / judul / outlet / PO / SPH…"
            className="pl-8 pr-8 py-2 border border-slate-300 text-xs w-72 focus:outline-none focus:border-[#FF5A1F] bg-white"
          />
          {q && <button data-testid="wmd-search-clear" onClick={() => setQ("")} className="absolute right-2 text-slate-400 hover:text-[#FF5A1F]"><XCircle size={13} /></button>}
        </div>
        {/* 149-cw-r178b: filter Outlet & PIC bisa diketik utk mencari (SearchableSelect). */}
        <div className="w-52">
          <SearchableSelect
            testid="wmd-filter-outlet"
            value={outletFilter}
            onChange={setOutletFilter}
            placeholder="Semua Outlet"
            searchPlaceholder="Ketik nama outlet…"
            options={[{ value: "all", label: "Semua Outlet" }, ...outletList.map((o) => ({ value: o, label: o }))]}
          />
        </div>
        <div className="w-52">
          <SearchableSelect
            testid="wmd-filter-pic"
            value={picFilter}
            onChange={setPicFilter}
            placeholder="Semua PIC"
            searchPlaceholder="Ketik nama PIC…"
            options={[{ value: "all", label: "Semua PIC" }, ...picList.map((o) => ({ value: o, label: o }))]}
          />
        </div>
        <MultiSelectStages value={stageFilter} onChange={setStageFilter} options={STAGE_FILTER_OPTIONS} />
        <SelectMini value={urgentFilter} onChange={setUrgentFilter} testid="wmd-filter-urgent" placeholder="Urgent" allLabel="Semua" options={[{ value: "urgent", label: "Urgent Case" }, { value: "non_urgent", label: "Non Urgent Case" }, { value: "undetermined", label: "Belum Diklasifikasikan" }]} />
        <div className="inline-flex border border-slate-300" data-testid="wmd-status-toggle">
          {[
            { k: "all", l: "Semua" }, { k: "aktif", l: "Aktif" },
            { k: "closed", l: "Closed" }, { k: "cancelled_owner", l: "Dibatalkan Owner" },
          ].map((s, i) => (
            <button key={s.k} type="button" data-testid={`wmd-status-${s.k}`} onClick={() => setStatusFilter(s.k)}
              className={`px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold ${i > 0 ? "border-l border-slate-300" : ""} ${statusFilter === s.k ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{s.l}</button>
          ))}
        </div>
        <button type="button" data-testid="wmd-reset-filters" onClick={resetFilters}
          className="inline-flex items-center gap-1 h-9 px-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#FF5A1F]">
          <RotateCcw size={11} /> Reset
        </button>
        <div className="ml-auto text-[10px] text-slate-500 tabular-nums flex items-center gap-2" data-testid="wmd-count">
          {selectedIds.size > 0 && (
            <>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#0B1D3A] bg-slate-100 text-[#0B1D3A] font-semibold" data-testid="wmd-selected-count">
              {selectedIds.size} dipilih
              <button
                type="button"
                data-testid="wmd-clear-selected"
                onClick={() => setSelectedIds(new Set())}
                className="text-[#0B1D3A] hover:text-slate-700"
                title="Batalkan pilihan"
              >
                <XCircle size={11} />
              </button>
            </span>
            {/* 2026-06-20 (user request): fasilitas admin isi kategori WO yang
                Belum Diklasifikasikan — bulk, dgn preview + konfirmasi. */}
            {canLedger && (
              <button
                type="button"
                data-testid="wmd-bulk-category-btn"
                onClick={() => setCatDlg(true)}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-amber-400 bg-amber-50 text-amber-800 font-semibold hover:bg-amber-100 transition-colors"
                title="Tentukan kategori (Urgent / Non-Urgent) untuk WO terpilih yang belum diklasifikasikan"
              >
                <Tag size={11} /> Tentukan Kategori
              </button>
            )}
            </>
          )}
          <span>{sorted.length} WO · Page {page}/{totalPages}</span>
        </div>
      </div>

      {/* Master-Detail Grid: 60/40 (widened kanan supaya detail panel muat) */}
      <div className="grid gap-4" style={{ gridTemplateColumns: "minmax(0, 60fr) minmax(0, 40fr)" }} data-testid="wmd-grid">
        {/* LEFT — List */}
        <section className="bg-white border border-slate-200" data-testid="wmd-list">
          <table className="w-full text-[11px] table-fixed">
            <colgroup>
              <col style={{ width: "32px" }} />
              {WO_COLUMNS.filter((c) => showCol(c.key)).map((c) => (
                <col key={c.key} style={{ width: `${c.widthPct}%` }} />
              ))}
              <col style={{ width: "40px" }} />
            </colgroup>
            <thead className="bg-slate-50 border-b border-slate-200 ens-sticky-thead">
              <tr className="text-left">
                <th className="px-2 py-2 text-center">
                  <input
                    type="checkbox"
                    data-testid="wmd-select-all"
                    checked={sorted.length > 0 && sorted.every((p) => selectedIds.has(p.id))}
                    ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && !sorted.every((p) => selectedIds.has(p.id)); }}
                    onChange={(e) => {
                      const next = new Set(selectedIds);
                      if (e.target.checked) sorted.forEach((p) => next.add(p.id));
                      else sorted.forEach((p) => next.delete(p.id));
                      setSelectedIds(next);
                    }}
                    title="Pilih semua WO terfilter"
                    className="cursor-pointer"
                  />
                </th>
                {showCol("po")        && <th className="px-2 py-2"><SortBtn k="po" label="No. PO" /></th>}
                {showCol("wo")        && <th className="px-2 py-2"><SortBtn k="wo" label="WO / Judul" /></th>}
                {showCol("outlet")    && <th className="px-2 py-2"><SortBtn k="outlet" label="Outlet" /></th>}
                {showCol("stage")     && <th className="px-2 py-2"><SortBtn k="stage" label="Tahap Saat Ini" /></th>}
                {showCol("action")    && <th className="px-2 py-2 uppercase text-[10px] tracking-wider font-semibold text-slate-600">Kategori WO</th>}
                {showCol("activity")  && <th className="px-2 py-2 uppercase text-[10px] tracking-wider font-semibold text-slate-600">Aktivitas Terakhir</th>}
                {showCol("wait_days") && <th className="px-2 py-2 uppercase text-[10px] tracking-wider font-semibold text-slate-600 text-right">Hari Menunggu</th>}
                {showCol("target")    && <th className="px-2 py-2"><SortBtn k="target" label="Tanggal Selesai Pekerjaan" /></th>}
                <th className="px-1 py-2 text-center uppercase text-[10px] tracking-wider font-semibold text-slate-600">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr><td colSpan={visibleColKeys.length + 2} className="text-center py-8 text-slate-400 italic" data-testid="wmd-empty">
                  {outletFilter !== "all"
                    ? "Belum ada Working Order pada parent/outlet ini."
                    : "Tidak ada WO sesuai filter."}
                </td></tr>
              )}
              {paged.map((p) => {
                const isActive = p.id === selectedId;
                const stage = WO_STAGES.find((s) => s.key === p.__stage);
                const target = p.project_done_date;
                const bucketKey = deriveActionBucket(p);
                const bucketMeta = ACTION_BUCKET_META[bucketKey];
                const manualAction = (p.perlu_tindakan_manual || "").trim();
                const lastAct = deriveLastActivity(p);
                const waitDays = woWaitDays(p, lastAct);
                const bucketCls =
                  bucketMeta?.tone === "amber"  ? "bg-amber-50 border-amber-300 text-amber-800" :
                  bucketMeta?.tone === "rose"   ? "bg-rose-50 border-rose-300 text-rose-800" :
                  bucketMeta?.tone === "orange" ? "bg-orange-50 border-orange-300 text-orange-800" :
                                                  "bg-slate-50 border-slate-300 text-slate-700";
                return (
                  <tr key={p.id} onClick={() => handleSelectWo(p.id)}
                    data-testid={`wmd-row-${p.id}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isActive ? "bg-slate-100 border-l-4 border-l-[#0B1D3A]" : "border-l-4 border-l-transparent"}`}>
                    <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`wmd-select-${p.id}`}
                        checked={selectedIds.has(p.id)}
                        onChange={(e) => {
                          const next = new Set(selectedIds);
                          if (e.target.checked) next.add(p.id); else next.delete(p.id);
                          setSelectedIds(next);
                        }}
                        className="cursor-pointer"
                      />
                    </td>
                    {showCol("po") && (
                    <td className="px-2 py-2" data-testid={`wmd-po-${p.id}`}>
                      {p.po_number ? (
                        <div className="font-mono text-[10px] font-semibold text-slate-700 truncate" title={p.po_number}>{p.po_number}</div>
                      ) : (
                        <span className="italic text-slate-300 text-[10px]">Belum ada PO</span>
                      )}
                    </td>
                    )}
                    {showCol("wo") && (
                    <td className="px-2 py-2">
                      <div className="font-mono font-semibold text-[#0B1D3A] truncate" title={p.wo_number}>{p.wo_number || "—"}</div>
                      <div className="text-[10px] text-slate-500 truncate" title={p.wo_title}>{p.wo_title || <span className="italic text-slate-300">Judul belum diisi</span>}</div>
                    </td>
                    )}
                    {showCol("outlet") && (
                    <td className="px-2 py-2 text-slate-700 truncate" title={outletKey(p)}>{outletKey(p)}</td>
                    )}
                    {showCol("stage") && (
                    <td className="px-2 py-2">
                      <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border truncate max-w-full bg-slate-50 border-slate-300 text-slate-700" title={p.__wf?.chevronLabel || stage?.label || p.__stage}>
                        {p.__wf?.chevronLabel || stage?.label || p.__stage}
                      </span>
                      {p.__wf?.needsVerification && (
                        <span
                          data-testid={`wmd-verify-${p.id}`}
                          className="ml-1 inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-amber-50 border-amber-300 text-amber-800"
                          title={(p.__wf?.verificationReasons || []).join("\n")}
                        >
                          Perlu Verifikasi
                        </span>
                      )}
                    </td>
                    )}
                    {showCol("action") && (
                    <td className="px-2 py-2">
                      {/* 149-cw-r86: kolom "Urgent case WO" — badge urgent (rose)
                          atau non-urgent (slate), + optional catatan manual. */}
                      <div className="flex flex-col gap-1">
                        {p.is_urgent === true ? (
                          <span
                            className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border bg-rose-100 border-rose-400 text-rose-800 max-w-max"
                            title="Ditandai sebagai Urgent Case"
                            data-testid={`wmd-urgent-flag-${p.id}`}
                          >
                            ⚑ Urgent
                          </span>
                        ) : p.is_urgent === false ? (
                          <span
                            className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-slate-50 border-slate-300 text-slate-600 max-w-max"
                            title="Non-urgent"
                          >
                            Non-urgent
                          </span>
                        ) : (
                          <span
                            className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-amber-50 border-amber-300 text-amber-800 max-w-max"
                            title="Kategori WO (Urgent / Non-Urgent) belum diklasifikasikan — admin bisa mengisi via checkbox baris → Tentukan Kategori"
                            data-testid={`wmd-undetermined-flag-${p.id}`}
                          >
                            Belum Diklasifikasikan
                          </span>
                        )}
                        {manualAction && (
                          <span
                            className="text-[10px] text-indigo-700 whitespace-normal leading-snug"
                            title={manualAction}
                            data-testid={`wmd-manual-action-${p.id}`}
                          >
                            {manualAction}
                          </span>
                        )}
                      </div>
                    </td>
                    )}
                    {showCol("activity") && (
                    <td className="px-2 py-2 text-[10px] text-slate-600" data-testid={`wmd-last-activity-${p.id}`}>
                      <div className="truncate font-medium text-slate-800" title={lastAct.label}>{lastAct.label}</div>
                      <div className="tabular-nums text-slate-500">{lastAct.date ? fmtDate(lastAct.date) : <span className="italic text-slate-300">—</span>}</div>
                    </td>
                    )}
                    {showCol("wait_days") && (
                    <td className="px-2 py-2 tabular-nums text-right" data-testid={`wmd-wait-days-${p.id}`}>
                      {waitDays == null ? <span className="text-slate-300">—</span> :
                        <span className={`inline-block text-[10px] font-semibold ${waitDays > 14 ? "text-rose-700" : waitDays > 7 ? "text-amber-700" : "text-slate-700"}`}>{waitDays} hari</span>}
                    </td>
                    )}
                    {showCol("target") && (
                    <td className="px-2 py-2 tabular-nums text-slate-700 text-[10px]">{target ? fmtDate(target) : <span className="text-slate-300">—</span>}</td>
                    )}
                    {/* 149-cw-r170: Quick Close — icon button kecil di ujung row supaya
                        user bisa close WO langsung tanpa buka panel detail / parent project. */}
                    <td className="px-1 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      {canClose && p.status === "active" && p.project_type !== "internal" && (
                        <button
                          type="button"
                          data-testid={`wmd-close-row-${p.id}`}
                          onClick={() => setCloseDlg({ open: true, project: p })}
                          className="inline-flex items-center justify-center w-6 h-6 border border-slate-300 text-slate-500 hover:border-[#0B1D3A] hover:text-[#0B1D3A] hover:bg-slate-100 transition-colors"
                          title="Close WO — tandai pekerjaan selesai / pembayaran diterima"
                        >
                          <RotateCcw size={12} className="rotate-180" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Pagination */}
          {sorted.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50 text-[11px]" data-testid="wmd-pagination">
              <button data-testid="wmd-prev-page" disabled={page <= 1} onClick={() => goToPage(page - 1)}
                className="px-2 py-1 border border-slate-300 uppercase tracking-wider font-semibold disabled:opacity-30">Sebelumnya</button>
              <span className="tabular-nums text-slate-500">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} dari {sorted.length}</span>
              <button data-testid="wmd-next-page" disabled={page >= totalPages} onClick={() => goToPage(page + 1)}
                className="px-2 py-1 border border-slate-300 uppercase tracking-wider font-semibold disabled:opacity-30">Berikutnya</button>
            </div>
          )}
        </section>

        {/* RIGHT — Detail sticky */}
        <aside className="bg-white border border-slate-200 ens-sticky-aside" data-testid="wmd-detail">
          {!selected ? (
            initialWoParam && notFoundRef.current === initialWoParam ? (
              <div className="p-8 text-center text-xs" data-testid="wmd-wo-not-found">
                <div className="text-rose-700 font-semibold mb-1">WO tidak ditemukan</div>
                <div className="text-slate-500 italic">Working Order dengan ID <span className="font-mono">{initialWoParam}</span> tidak tersedia, telah diarsipkan, atau tidak dapat diakses oleh Anda.</div>
              </div>
            ) : (
              <div className="p-8 text-center text-xs text-slate-400 italic">Pilih WO di sebelah kiri untuk lihat detail.</div>
            )
          ) : (
            <WODetailPanel
              wo={selected}
              canCancelWo={canCancelWo}
              canLedger={canLedger}
              canClose={canClose}
              onEdit={() => onOpenEdit?.(selected)}
              onOpenCancel={() => setCancelDlg({ open: true, project: selected })}
              onOpenReopen={() => setReopenDlg({ open: true, project: selected })}
              onOpenClose={() => setCloseDlg({ open: true, project: selected })}
              onReopenProject={async () => {
                try {
                  await api.patch(`/active-projects/${selected.id}/reopen`);
                  toast.success("WO dibuka kembali (Active)");
                  onChanged?.();
                } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Gagal reopen"); }
              }}
              onDelete={() => onDelete?.(selected)}
              onChanged={onChanged}
            />
          )}
        </aside>
      </div>

      <CancelByOwnerDialog
        open={cancelDlg.open}
        onOpenChange={(v) => setCancelDlg({ open: v, project: v ? cancelDlg.project : null })}
        project={cancelDlg.project}
        onDone={onChanged}
      />
      <ReopenCancelledDialog
        open={reopenDlg.open}
        onOpenChange={(v) => setReopenDlg({ open: v, project: v ? reopenDlg.project : null })}
        project={reopenDlg.project}
        onDone={onChanged}
      />
      {/* 149-cw-r162: Close Project dialog — dipakai bersama ProjectsPage. */}
      <CloseProjectDialog
        open={closeDlg.open}
        onOpenChange={(v) => setCloseDlg({ open: v, project: v ? closeDlg.project : null })}
        project={closeDlg.project}
        onDone={onChanged}
      />
      {/* 149-cw-r86: WO Stage Info Dialog — dibuka klik icon (i) di chevron. */}
      <WOStageInfoDialog
        open={!!infoStage}
        onOpenChange={(v) => { if (!v) setInfoStage(null); }}
        stage={infoStage}
        user={user}
      />
      {/* 2026-06-21 (user request): dialog export dengan PILIHAN KOLOM —
          checklist katalog lengkap (identitas, tahap, SPH/Nego per pengajuan,
          keuangan DPP/PPN/PPh/retensi/dst) + cakupan (semua filter/terpilih).
          Urutan export dikelompokkan per No. PO (PO sama berdekatan). */}
      <WOExportDialog
        open={exportDlg.open}
        mode={exportDlg.mode}
        filteredCount={sorted.length}
        selectedCount={selectedIds.size}
        maxSph={sphColumnCount(sorted)}
        onClose={() => setExportDlg({ open: false, mode: null })}
        onConfirm={({ keys, scope }) => {
          const data = scope === "selected" ? sorted.filter((p) => selectedIds.has(p.id)) : sorted;
          const { rows, moneyHeaders } = buildExportRowsV2(sortForExport(data), keys, deriveLastActivity);
          const suffix = scope === "selected" ? `${data.length} WO Terpilih` : "Daftar Aktif";
          if (exportDlg.mode === "pdf") {
            printWOsToPDF(rows, {
              title: `Monitoring Working Order — ${suffix}`,
              subtitle: `${data.length} WO · ${rows[0] ? Object.keys(rows[0]).length : 0} kolom · diurutkan per No. PO`,
              moneyCols: moneyHeaders,
            });
          } else {
            exportWOsToExcel(rows, {
              fileName: "wo-monitoring-daftar",
              sheetName: "WO Monitoring",
              title: `Monitoring Working Order — ${suffix}`,
            });
          }
          setExportDlg({ open: false, mode: null });
        }}
      />
    </div>
  );
}

// -------- Sub-components --------

const TONE = {
  slate: "bg-slate-50 border-slate-200 text-slate-700",
  rose: "bg-rose-50 border-rose-200 text-rose-800",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  orange: "bg-orange-50 border-orange-200 text-orange-800",
  sky: "bg-sky-50 border-sky-200 text-sky-800",
};

function KpiCard({ label, value, tone = "slate", testid }) {
  return (
    <div className={`border p-3 ${TONE[tone]}`} data-testid={testid}>
      <div className="overline text-[10px]">{label}</div>
      <div className="font-display text-xl tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

// R32E (2026-08-27): BucketCard — clickable filter card.
// 149-cw-r110 (2026-08-28) — Simplified palette:
//   Default idle: putih dgn border slate.
//   Active/selected: navy full.
//   Rose tone: urgent (semantic).
function BucketCard({ label, value, tone = "slate", active = false, onClick, testid }) {
  const cls =
    active                ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" :
    tone === "rose"       ? "bg-rose-50 border-rose-300 text-rose-800 hover:bg-rose-100" :
                            "bg-white border-slate-300 text-[#0B1D3A] hover:bg-slate-50";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`border-2 rounded-md px-3 py-2 text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#0B1D3A] ${cls}`}
      title={`Filter: ${label}`}
    >
      <div className="text-[9px] font-bold uppercase tracking-wider leading-tight break-words">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1 leading-none">{value}</div>
    </button>
  );
}

// 149-cw-r112 (2026-08-29) — Multi-select Popover untuk filter Tahap WO.
// value: array of stage keys; empty = "Semua Tahap".
// Klik badge chevron atau checkbox di dropdown akan toggle stage di array.
function MultiSelectStages({ value = [], onChange, options = [] }) {
  const [open, setOpen] = useState(false);
  const selected = Array.isArray(value) ? value : [];
  const count = selected.length;
  const label = count === 0
    ? "Semua Tahap"
    : count === 1
      ? (options.find((o) => o.value === selected[0])?.label || `1 tahap`)
      : `${count} tahap dipilih`;
  const toggle = (key) => {
    if (!onChange) return;
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="wmd-filter-stage"
          className="h-9 min-w-[180px] inline-flex items-center justify-between gap-2 border border-slate-300 px-2 text-xs bg-white hover:border-[#FF5A1F] focus:outline-none focus:border-[#FF5A1F]"
          title="Filter tahap WO (bisa pilih lebih dari satu)"
        >
          <span className="flex items-center gap-1.5 truncate">
            <Filter size={12} className="text-slate-400 shrink-0" />
            <span className={`truncate ${count > 0 ? "text-[#0B1D3A] font-semibold" : "text-slate-600"}`}>{label}</span>
          </span>
          {count > 0 && (
            <span
              role="button"
              tabIndex={0}
              data-testid="wmd-filter-stage-clear"
              onClick={(e) => { e.stopPropagation(); onChange && onChange([]); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange && onChange([]); } }}
              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 hover:bg-rose-100 hover:text-rose-700 cursor-pointer"
              title="Bersihkan filter tahap"
            >
              <XCircle size={12} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0" data-testid="wmd-filter-stage-panel">
        <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Filter Tahap WO</div>
          <button
            type="button"
            onClick={() => onChange && onChange([])}
            className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline"
            data-testid="wmd-filter-stage-reset"
          >
            Reset
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {options.map((o) => {
            const checked = selected.includes(o.value);
            return (
              <label
                key={o.value}
                data-testid={`wmd-filter-stage-opt-${o.value}`}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 ${checked ? "bg-slate-50 text-[#0B1D3A] font-semibold" : "text-slate-700"}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(o.value)}
                  className="w-3.5 h-3.5 accent-[#FF5A1F] cursor-pointer"
                />
                <span className="truncate">{o.label}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SelectMini({ value, onChange, testid, placeholder, options = [], allLabel }) {
  const isObj = options[0] && typeof options[0] === "object";
  // 149-cd: <option> hanya boleh berisi plain text. Label default = "Semua {placeholder}".
  // 149-cw-r97: opsi `allLabel` override — bila caller mau custom "Semua" label.
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="h-9 border border-slate-300 px-2 text-xs bg-white focus:outline-none focus:border-[#FF5A1F]">
      <option value="all">{allLabel || `Semua ${placeholder}`}</option>
      {options.map((o) => {
        const v = isObj ? o.value : o;
        const l = isObj ? o.label : o;
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

function Row({ k, v, tone = "slate", testid }) {
  const cls = tone === "rose" ? "text-rose-700" : tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : tone === "indigo" ? "text-indigo-700 font-semibold" : "text-slate-800";
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-slate-100 last:border-b-0" data-testid={testid}>
      <span className="overline text-[10px] text-slate-500">{k}</span>
      <span className={`text-xs tabular-nums font-medium text-right ${cls}`}>{v}</span>
    </div>
  );
}

function TimelineItem({ label, date, done, active }) {
  return (
    <div className="flex items-start gap-2">
      <div className={`mt-1 w-2.5 h-2.5 shrink-0 border ${done ? "bg-emerald-500 border-emerald-600" : active ? "bg-[#FF5A1F] border-[#FF5A1F]" : "bg-white border-slate-300"}`} />
      <div className="flex-1 min-w-0 pb-2 border-b border-slate-100">
        <div className={`text-xs ${done ? "text-slate-800 font-semibold" : active ? "text-[#0B1D3A] font-semibold" : "text-slate-400"}`}>{label}</div>
        <div className="text-[10px] tabular-nums text-slate-500">{date ? fmtDate(date) : <span className="italic text-slate-300">Belum tercatat</span>}</div>
      </div>
    </div>
  );
}

function WODetailPanel({ wo, canCancelWo, canLedger, canClose, onEdit, onOpenCancel, onOpenReopen, onOpenClose, onReopenProject, onDelete, onChanged }) {
  const stage = WO_STAGES.find((s) => s.key === wo.__stage);
  // 16-WO-LIFECYCLE-FINANCIAL-PARITY-R1: status keuangan terpisah dari operasional.
  const finStatus = deriveWOFinancialStatus(wo);
  const invoiceEntries = (wo.income_entries || []).filter((e) => e.invoice_date && !e.is_retention_claim);
  const totalInvoice = invoiceEntries.reduce((s, e) => s + (Number(e.gross_invoice_amount || e.dpp_amount || e.amount) || 0), 0);
  const receivedVerified = invoiceEntries.filter((e) => e.date && !e.waiting_payment).reduce((s, e) => s + (Number(e.net_received_amount || e.gross_invoice_amount || e.amount) || 0), 0);
  // 16-WO-PAYMENT-TAX-R1: outstanding = Tagihan Kas − Diterima. PPh dan retensi
  // yang tercantum pada invoice bukan piutang client → dikurangi dari gross.
  const { cashDue } = computeWOCashDue(wo);
  const pphTotal = invoiceEntries.reduce((s, e) => s + (Number(e.pph_amount) || 0), 0);
  const retentionOnInvoice = invoiceEntries.reduce((s, e) => s + (Number(e.retention_amount) || 0), 0);
  const outstanding = Math.max(0, cashDue - receivedVerified);
  const retHeld = computeRetentionHeld(wo, "separate");
  const retClaimed = computeRetentionClaimed(wo);
  const retOutstanding = Math.max(0, retHeld - retClaimed);
  const retentionDue = (() => {
    const days = Number(wo.retention_period_days) || 0;
    if (wo.status !== "completed" || !days || !wo.completed_at) return null;
    const d = new Date(wo.completed_at); d.setDate(d.getDate() + days); return d;
  })();
  const invoiceCreated = invoiceEntries[0]?.invoice_date || null;
  const invoiceSent = wo.invoice_sent_date || null;
  const paymentDue = invoiceEntries.find((e) => e.due_date)?.due_date || null;
  const hasInvoiceRecord = !!invoiceCreated;
  const paymentOverdue = (() => {
    if (!paymentDue) return false;
    try { return new Date() > new Date(paymentDue); } catch { return false; }
  })();

  // 16-WO-LIFECYCLE-FINANCIAL-PARITY-R1: Next Action heuristics baru — tidak
  // pernah menampilkan "Terbitkan Invoice" karena aplikasi ini tidak menerbitkan
  // invoice. Prioritaskan status keuangan aktual atas operasional.
  const nextAction = (() => {
    if (wo.status === "cancelled_by_owner") return { label: "WO Dibatalkan Owner", detail: wo.cancelled_reason || "", target: wo.cancelled_at };
    if (wo.status === "completed") return { label: "WO Sudah Closed", detail: "Cek retensi & tagihan tertahan", target: retentionDue?.toISOString() };
    // Status keuangan → tindakan
    if (finStatus.key === "PAID") {
      if (!wo.invoice_sent_date && hasInvoiceRecord) {
        return { label: "Rekonsiliasi lifecycle", detail: "Pembayaran penuh — lengkapi tanggal invoice dikirim", target: invoiceCreated };
      }
      return { label: "Tutup WO / lanjut Closing", detail: "Semua tagihan lunas — verifikasi retensi & syarat closing", target: retentionDue?.toISOString() };
    }
    if (finStatus.key === "PARTIAL") {
      return { label: "Follow-up sisa pembayaran", detail: `Sisa: ${formatIDR(outstanding)}`, target: paymentDue };
    }
    if (finStatus.key === "NEEDS_REVIEW") {
      return { label: "Verifikasi data pembayaran", detail: "Ada catatan penerimaan tanpa tanggal invoice yang jelas", target: null };
    }
    if (finStatus.key === "WAITING") {
      if (paymentOverdue) return { label: "Follow-up collection", detail: "Invoice sudah jatuh tempo — belum ada pembayaran", target: paymentDue };
      return { label: "Tunggu pembayaran", detail: "Invoice sudah dicatat; monitor collection", target: paymentDue };
    }
    // finStatus = UNBILLED
    if (hasInvoiceRecord && !wo.invoice_sent_date) {
      return { label: "Lengkapi tanggal invoice dikirim", detail: "Invoice sudah dibuat di Accurate; belum ada tanggal kirim", target: null };
    }
    if (wo.__stage === "selesai_fisik") {
      return { label: "Catat data invoice dari Accurate", detail: "Pekerjaan selesai; tambahkan Invoice Reference dari Accurate", target: wo.project_done_date };
    }
    if (wo.__stage === "wo_diterima") return { label: "Jadwalkan Survey / Kirim SPH", detail: "Belum ada tanggal survey/SPH", target: wo.end_date };
    if (wo.__stage === "site_survey") return { label: "Kirim SPH ke Client", detail: "Site survey selesai — susun & kirim SPH", target: wo.end_date };
    if (wo.__stage === "sph_negosiasi" || wo.__stage === "penerbitan_po" || wo.__stage === "sph_negosiasi_po") {
      const sub = deriveSphSubStatus(wo);
      if (sub === "sph_not_sent") return { label: "Kirim SPH Pertama", detail: "SPH belum pernah dikirim", target: wo.end_date };
      if (sub === "sph_sent") return { label: "Follow-up SPH ke Client", detail: "SPH sudah dikirim; tunggu tanggapan", target: wo.end_date };
      if (sub === "sph_negotiation") return { label: "Selesaikan Negosiasi / Revisi SPH", detail: "Ada revisi terbuka — kirim versi berikutnya", target: wo.end_date };
      if (sub === "sph_approved_wait_po") return { label: "Tagih PO ke Client", detail: "SPH disetujui — PO belum terbit", target: wo.end_date };
      return { label: "Follow-up PO ke Client", detail: "SPH sudah dikirim; PO belum diterima", target: wo.end_date };
    }
    // Legacy key backward-compat
    if (wo.__stage === "menunggu_po") return { label: "Follow-up PO ke Client", detail: "SPH sudah dikirim; PO belum diterima", target: wo.end_date };
    if (wo.__stage === "pengerjaan") return { label: "Pekerjaan Berjalan", detail: "PO sudah diterima; kejar target selesai", target: wo.project_done_date || wo.end_date };
    return { label: "—", detail: "", target: null };
  })();

  const isCancelled = wo.status === "cancelled_by_owner";
  const canBeCancelled = !isCancelled && wo.status !== "completed";

  return (
    <div className="p-4 space-y-4" data-testid={`wmd-detail-panel-${wo.id}`}>
      {/* A. Identitas */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${stage?.badge}`} data-testid={`wmd-badge-operational-${wo.id}`}>{stage?.label}</span>
          <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${finStatus.cls}`} data-testid={`wmd-badge-financial-${wo.id}`}>{finStatus.label}</span>
        </div>
        <div className="mt-2 flex items-start justify-between gap-2">
          <div className="font-display text-lg text-[#0B1D3A] leading-tight tabular-nums flex-1">{wo.wo_number || "—"}</div>
          <AuditLogButton feature_key="project" target_id={wo.id} title={wo.wo_number || wo.project_name || "WO"} size="sm" />
        </div>
        <div className="text-sm text-slate-700 mt-0.5">{wo.wo_title || <span className="italic text-slate-400">Judul belum diisi</span>}</div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
          <div><span className="overline text-slate-500">Outlet</span><div className="font-semibold text-slate-800">{outletKey(wo)}</div></div>
          <div><span className="overline text-slate-500">Client</span><div className="text-slate-700">{wo.client || "—"}</div></div>
          <div><span className="overline text-slate-500">PIC</span><div className="text-slate-700">{wo.created_by_name || "—"}</div></div>
          <div><span className="overline text-slate-500">Status DB</span><div className="text-slate-700">{PROJECT_STATUS[wo.status]?.label || wo.status || "active"}</div></div>
        </div>
      </div>

      {/* B. Timeline */}
      <div>
        <div className="overline text-slate-600 mb-2">Timeline Tahapan</div>
        <div className="space-y-0" data-testid="wmd-timeline">
          <TimelineItem label="WO Diterima" date={wo.wo_date || wo.created_at} done={true} />
          <TimelineItem label="Site Survey" date={wo.site_survey_date} done={!!wo.site_survey_date} active={wo.__stage === "site_survey"} />
          {/* 149-cw-r94 (2026-08-27): SPH/Negosiasi/PO dipisah menjadi 2
              TimelineItem — "SPH & Negosiasi" + "Penerbitan PO". Cumulative
              highlight: setiap milestone yang sudah dilewati tetap hijau. */}
          {(() => {
            const sub = deriveSphSubStatus(wo);
            const meta = SPH_SUB_STATUS[sub] || SPH_SUB_STATUS.sph_not_sent;
            const subs = Array.isArray(wo.sph_submissions) ? wo.sph_submissions.filter((r) => r?.date) : [];
            const latestDate = subs.length > 0
              ? [...subs].sort((a, b) => (a.date || "").localeCompare(b.date || "")).slice(-1)[0]?.date
              : (wo.sph_submit_date || null);
            const hasSph = !!(wo.sph_submit_date || subs.length > 0);
            // "SPH & Negosiasi" — done jika SPH sudah pernah dikirim (cumulative).
            const sphNegDone = hasSph;
            const sphNegActive = wo.__stage === "sph_negosiasi";
            // "Penerbitan PO" — done jika po_number terisi (cumulative).
            //   Menyala (active) jika SPH latest = approved atau __stage === penerbitan_po.
            const penerbitanDone = !!wo.po_number;
            const penerbitanActive = wo.__stage === "penerbitan_po";
            return (
              <div data-testid="wmd-timeline-sph-po">
                <TimelineItem label="SPH & Negosiasi" date={latestDate} done={sphNegDone} active={sphNegActive} />
                {(sphNegDone || sphNegActive) && subs.length > 0 && (
                  <div className="ml-4 -mt-1 mb-2 pl-3 border-l border-slate-200 space-y-1" data-testid="wmd-timeline-sph-detail">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${meta.cls}`} data-testid="wmd-sph-substatus">
                        {meta.label}
                      </span>
                      <span className="text-[10px] text-slate-500 tabular-nums" data-testid="wmd-sph-version-count">
                        {subs.length} versi SPH
                      </span>
                    </div>
                    <ol className="text-[10px] text-slate-600 space-y-0.5" data-testid="wmd-sph-history">
                      {[...subs].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 4).map((s, idx) => {
                        const ver = subs.length - idx;
                        return (
                          <li key={s.id || `${s.date}-${idx}`} className="tabular-nums" data-testid={`wmd-sph-history-${idx}`}>
                            <span className="font-semibold text-[#0B1D3A]">v{ver}</span>
                            <span className="mx-1">·</span>
                            <span>{fmtDate(s.date)}</span>
                            {s.offer_value != null && s.offer_value !== "" && (
                              <>
                                <span className="mx-1">·</span>
                                <span>{formatIDR(s.offer_value)}</span>
                              </>
                            )}
                            {s.note && (
                              <>
                                <span className="mx-1">·</span>
                                <span className="italic text-slate-500">{s.note}</span>
                              </>
                            )}
                          </li>
                        );
                      })}
                      {subs.length > 4 && <li className="text-slate-400 italic">+{subs.length - 4} versi lain</li>}
                    </ol>
                  </div>
                )}
                <TimelineItem label="Penerbitan PO" date={wo.po_date || (sub === "sph_approved_wait_po" ? latestDate : null)} done={penerbitanDone} active={penerbitanActive} />
                {wo.po_number && (
                  <div className="ml-4 -mt-1 mb-2 pl-3 border-l border-slate-200" data-testid="wmd-po-info">
                    <div className="text-[10px] text-slate-600 tabular-nums">
                      <span className="font-semibold text-emerald-700">PO Terbit</span>
                      <span className="mx-1">·</span>
                      <span className="font-mono">{wo.po_number}</span>
                      {wo.po_date && (
                        <>
                          <span className="mx-1">·</span>
                          <span>{fmtDate(wo.po_date)}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <TimelineItem label="Pengerjaan" date={wo.po_date} done={!!wo.project_done_date} active={wo.__stage === "pengerjaan"} />
          <TimelineItem label="Selesai Fisik" date={wo.project_done_date} done={!!wo.project_done_date} active={wo.__stage === "selesai_fisik"} />
          {/* 16-WO-INVOICE-SPLIT-R1: Invoice timeline sesuai policy DPP.
              DPP ≤ threshold → 1 invoice 100%. DPP > threshold → 95% + 5% retensi. */}
          {(() => {
            const policy = getWOInvoicePolicy(wo);
            const nonRetInvoice = (wo.income_entries || []).find((e) => e && e.invoice_date && !e.is_retention_claim);
            const retInvoice = (wo.income_entries || []).find((e) => e && e.invoice_date && e.is_retention_claim);
            const mainInvoiceDate = (nonRetInvoice && nonRetInvoice.invoice_date) || wo.invoice_sent_date || null;
            const retInvoiceDate = retInvoice && retInvoice.invoice_date;
            if (!policy.isSplit) {
              return (
                <TimelineItem
                  label="Invoice 100%"
                  date={mainInvoiceDate}
                  done={!!mainInvoiceDate}
                  active={wo.__stage === "invoice_full"}
                />
              );
            }
            return (
              <>
                <TimelineItem
                  label="Invoice 95%"
                  date={mainInvoiceDate}
                  done={!!mainInvoiceDate}
                  active={wo.__stage === "invoice_95"}
                />
                <TimelineItem
                  label="Invoice 5% (Retensi)"
                  date={retInvoiceDate}
                  done={!!retInvoiceDate}
                  active={wo.__stage === "invoice_5_retention"}
                />
              </>
            );
          })()}
          <TimelineItem
            label={isCancelled ? "Dibatalkan Owner" : "Closed"}
            date={isCancelled ? wo.cancelled_at : wo.completed_at}
            done={isCancelled || !!wo.completed_at}
          />
        </div>
      </div>

      {/* C. Komersial */}
      <div>
        <div className="overline text-slate-600 mb-1">Data Komersial</div>
        <Row k="Nomor PO" v={wo.po_number || "—"} />
        <Row k="Nilai PO" v={wo.po_value != null ? formatIDR(wo.po_value) : "—"} />
        <Row k="Nilai Pekerjaan (Kontrak)" v={wo.contract_value != null ? formatIDR(wo.contract_value) : "—"} />
        <Row k="Retention %" v={(Number(wo.retention_pct) || 0) + "%"} />
        <Row k="Jatuh Tempo Retensi" v={retentionDue ? fmtDate(retentionDue.toISOString()) : "—"} />
      </div>

      {/* D. Tanggal Penting */}
      <div>
        <div className="overline text-slate-600 mb-1">Tanggal Penting</div>
        <Row k="SPH Submit" v={fmtDate(wo.sph_submit_date)} />
        <Row k="Site Survey" v={fmtDate(wo.site_survey_date)} />
        <Row k="Target Selesai" v={fmtDate(wo.end_date || wo.project_done_date)} />
        <Row k="Project Done" v={fmtDate(wo.project_done_date)} />
        <Row k="Invoice Dibuat" v={fmtDate(invoiceCreated)} />
        <Row k="Invoice Dikirim" v={fmtDate(invoiceSent)} />
        <Row k="Jatuh Tempo Bayar" v={fmtDate(paymentDue)} />
      </div>

      {/* E. Keuangan */}
      <div>
        <div className="overline text-slate-600 mb-1">Status Keuangan <span className="text-[9px] normal-case text-slate-400 italic">(read-only · dari Accurate)</span></div>
        <Row k="Total Invoice" v={formatIDR(totalInvoice)} />
        {pphTotal > 0 && <Row k="− PPh Dipotong" v={formatIDR(pphTotal)} tone="slate" />}
        {retentionOnInvoice > 0 && <Row k="− Retensi (ditahan)" v={formatIDR(retentionOnInvoice)} tone="slate" />}
        <Row k="Tagihan Kas" v={formatIDR(cashDue)} tone="indigo" testid="wmd-cash-due" />
        <Row k="Diterima (verified)" v={formatIDR(receivedVerified)} tone="emerald" />
        <Row k="Outstanding" v={formatIDR(outstanding)} tone={outstanding > 0 ? "rose" : "slate"} testid="wmd-outstanding" />
        <Row k="Retensi Outstanding" v={formatIDR(retOutstanding)} tone={retOutstanding > 0 ? "amber" : "slate"} />
      </div>

      {/* F. Next Action */}
      {!isCancelled && (
        <div className="bg-orange-50 border border-orange-200 border-l-4 border-l-[#FF5A1F] p-3" data-testid="wmd-next-action">
          <div className="overline text-orange-700 text-[10px]">Next Action</div>
          {(wo.perlu_tindakan_manual || "").trim() && (
            <div
              className="mt-1 mb-2 bg-indigo-50 border border-indigo-200 border-l-2 border-l-indigo-500 px-2 py-1.5"
              data-testid={`wmd-manual-action-note-${wo.id}`}
            >
              <div className="overline text-indigo-700 text-[9px]">Catatan Manual (override)</div>
              <div className="text-[12px] text-indigo-900 whitespace-pre-wrap leading-snug">{wo.perlu_tindakan_manual}</div>
            </div>
          )}
          <div className="mt-1 font-semibold text-[#0B1D3A] text-sm flex items-center gap-1.5"><ChevronsRight size={13} /> {nextAction.label}</div>
          {nextAction.detail && <div className="text-[11px] text-slate-600 mt-0.5">{nextAction.detail}</div>}
          <div className="mt-1 text-[10px] text-slate-500 tabular-nums">
            {wo.created_by_name && <>PIC: <b className="text-slate-700">{wo.created_by_name}</b> · </>}
            Target: <b className="text-slate-700">{fmtDate(nextAction.target)}</b>
          </div>
        </div>
      )}

      {/* Cancel-by-Owner metadata card */}
      {isCancelled && (
        <div className="bg-slate-50 border border-slate-300 border-l-4 border-l-slate-500 p-3 space-y-1" data-testid="wmd-cancelled-meta">
          <div className="overline text-slate-700">Dibatalkan Pemberi Kerja</div>
          <div className="text-[11px] text-slate-600">Tanggal: <b className="text-slate-800 tabular-nums">{fmtDate(wo.cancelled_at)}</b></div>
          <div className="text-[11px] text-slate-600">Kategori: <b className="text-slate-800">{CANCEL_REASON_LABELS[wo.cancelled_reason_category] || wo.cancelled_reason_category || "—"}</b></div>
          <div className="text-[11px] text-slate-600">Alasan: {wo.cancelled_reason || "—"}</div>
          {wo.cancelled_note && <div className="text-[11px] text-slate-500 italic">{wo.cancelled_note}</div>}
          {wo.cancelled_owner_confirmed_by && <div className="text-[10px] text-slate-500">Konfirmasi: {wo.cancelled_owner_confirmed_by}</div>}
          {wo.cancelled_billable && <div className="text-[10px] text-amber-700">Ditagihkan: {formatIDR(wo.cancelled_billable_amount || 0)}</div>}
          <div className="text-[9px] text-slate-400">Dicatat oleh {wo.cancelled_by_name || "—"}</div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap pt-2 border-t border-slate-200">
        <Button data-testid={`wmd-open-edit-${wo.id}`} variant="outline" size="sm" onClick={onEdit}
          className="rounded-none border-sky-500 text-sky-700 hover:bg-sky-50 uppercase text-[10px] tracking-wider font-semibold">
          <Pencil size={11} className="mr-1" /> Buka Detail
        </Button>
        {canLedger && !isCancelled && wo.status !== "completed" && (
          <Link
            to={`/finance-report?tab=invoice-piutang&project_id=${encodeURIComponent(wo.id)}`}
            data-testid={`wmd-open-finance-${wo.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-[#FF5A1F] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white"
            title="Kelola invoice & pembayaran di Laporan Keuangan"
          >
            Laporan Keuangan →
          </Link>
        )}
        {canCancelWo && canBeCancelled && (
          <Button data-testid={`wmd-cancel-owner-${wo.id}`} size="sm" onClick={onOpenCancel}
            className="rounded-none bg-rose-700 hover:bg-rose-800 text-white uppercase text-[10px] tracking-wider font-semibold">
            <Ban size={11} className="mr-1" /> Batalkan oleh Owner
          </Button>
        )}
        {/* 149-cw-r162: Close WO — tersedia saat WO aktif (bukan cancelled, bukan sudah completed).
            Setelah pembayaran diterima penuh atau pekerjaan dianggap selesai, user klik Close
            untuk mark WO sebagai `completed`. Backend PATCH /active-projects/{pid}/close.
            Butuh perm `projects.close` (terpisah dari delete). */}
        {canClose && !isCancelled && wo.status !== "completed" && wo.project_type !== "internal" && (
          <Button data-testid={`wmd-close-wo-${wo.id}`} size="sm" onClick={onOpenClose}
            className="rounded-none bg-[#0B1D3A] hover:bg-[#1a2e52] text-white uppercase text-[10px] tracking-wider font-semibold"
            title="Tutup WO — pembayaran diterima / pekerjaan selesai">
            <RotateCcw size={11} className="mr-1 rotate-180" /> Close WO
          </Button>
        )}
        {/* 149-cw-r162: Reopen Closed WO — kembali ke `active` kalau perlu tambah data/koreksi. */}
        {canClose && wo.status === "completed" && !isCancelled && (
          <Button data-testid={`wmd-reopen-wo-${wo.id}`} size="sm" onClick={onReopenProject}
            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white uppercase text-[10px] tracking-wider font-semibold"
            title="Buka kembali WO yang sudah closed">
            <RotateCcw size={11} className="mr-1" /> Buka Kembali WO
          </Button>
        )}
        {canCancelWo && isCancelled && (
          <Button data-testid={`wmd-reopen-cancelled-${wo.id}`} size="sm" onClick={onOpenReopen}
            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white uppercase text-[10px] tracking-wider font-semibold">
            <RotateCcw size={11} className="mr-1" /> Buka Kembali WO
          </Button>
        )}
        {canCancelWo && onDelete && (
          <Button data-testid={`wmd-delete-wo-${wo.id}`} size="sm" onClick={onDelete}
            className="rounded-none bg-slate-800 hover:bg-slate-900 text-white uppercase text-[10px] tracking-wider font-semibold ml-auto"
            title="Hapus WO — butuh alasan wajib (audit log)">
            <Trash2 size={11} className="mr-1" /> Hapus WO
          </Button>
        )}
      </div>
    </div>
  );
}
